import React, { useState, useEffect } from 'react';
import { defineMessages } from 'react-intl';
import Clear from '@material-ui/icons/Clear';
import { bool, func, object, string } from 'prop-types';
import Button from '@alltrails/shared/denali/components/Button';

import Accordion from '../../../shared/Accordion';
import { PaperSize, Orientation, Grid, Scale } from '.';
import CombinedShape from '../../../../../assets/images/icons/combined-shape.svg';

import LegacyModal from '../../../LegacyModal';
import CustomProvider from 'components/CustomProvider';
import useFormatMessage from 'hooks/useFormatMessage';

const PRINT_SETTINGS_STRINGS = defineMessages({
  PAPER_SIZE: { defaultMessage: 'Paper size' },
  ORIENTATION: { defaultMessage: 'Orientation' },
  GRID: { defaultMessage: 'Grid' },
  SCALE: { defaultMessage: 'Scale' },
  PRINT_SETTINGS: { defaultMessage: 'Print settings' }
});

const PrintSettings = ({ toggleAccordionState, accordionsOpened, title, isMobileWidth, onSave, areSettingsChanged, resetToInitialSettings }) => {
  const {
    formattedDefaultMessages: { PAPER_SIZE, ORIENTATION, GRID, SCALE, PRINT_SETTINGS }
  } = useFormatMessage(PRINT_SETTINGS_STRINGS);
  const [visible, setVisible] = useState(false);

  const handleKeyDown = e => {
    if (visible && e.keyCode == 27) {
      setVisible(false); // Esc
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  });

  const handleCloseModal = () => {
    setVisible(false);
    resetToInitialSettings();
  };

  const renderPrintSettings = () => {
    return (
      <CustomProvider>
        <div className="settingsContainer">
          <Accordion
            onClick={() => toggleAccordionState('paperSize')}
            isOpen={accordionsOpened.paperSize}
            title={PAPER_SIZE}
            className="printComponent"
          >
            <PaperSize isMobileWidth={isMobileWidth} />
          </Accordion>
          <Accordion
            onClick={() => toggleAccordionState('orientation')}
            isOpen={accordionsOpened.orientation}
            title={ORIENTATION}
            className="printComponent"
          >
            <Orientation isMobileWidth={isMobileWidth} />
          </Accordion>
          <Accordion
            onClick={() => toggleAccordionState('grid')}
            isOpen={accordionsOpened.grid}
            title={GRID}
            className="printComponent"
          >
            <Grid isMobileWidth={isMobileWidth} />
          </Accordion>
          <Accordion
            onClick={() => toggleAccordionState('scale')}
            isOpen={accordionsOpened.scale}
            title={SCALE}
            className="printComponent"
          >
            <Scale isMobileWidth={isMobileWidth} />
          </Accordion>
        </div>
      </CustomProvider>
    );
  };

  const renderModal = () => {
    if (!isMobileWidth) {
      return renderPrintSettings();
    }
    if (!visible) {
      return null;
    }

    // TODO: DS-95 replace clear button with icon button
    return (
      <LegacyModal handleBackgroundClick={() => setVisible(false)}>
        <div className="printSettingsModal">
          <div className="header">
            <button type="button" className="clearIcon" onClick={handleCloseModal} data-testid="closeIcon">
              <Clear />
            </button>
            <span className="title"> {title} </span>
          </div>
          {renderPrintSettings()}
          <div className="saveContainer">
            <Button text={SAVE} disabled={!areSettingsChanged} onClick={onSave} testId="print-page-save" variant="primary" />
          </div>
        </div>
      </LegacyModal>
    );
  };

  return (
    <div className={!isMobileWidth ? 'printSettings' : 'printSettingsIconContainer'}>
      {isMobileWidth && (
        <img className="settingsIcon" alt={PRINT_SETTINGS} src={CombinedShape} onClick={() => setVisible(true)} />
      )}
      {renderModal()}
    </div>
  );
};

PrintSettings.propTypes = {
  toggleAccordionState: func,
  accordionsOpened: object,
  title: string,
  onSave: func,
  areSettingsChanged: bool,
  resetToInitialSettings: func
};

PrintSettings.defaultProps = {
  toggleAccordionState: () => {},
  accordionsOpened: {},
  title: '',
  onSave: () => {},
  areSettingsChanged: false,
  resetToInitialSettings: () => {}
};

export default PrintSettings;
